// "Khoản chi của tôi" (QĐ 0283) — nhân viên tự khai khoản tự bỏ tiền hoặc trừ
// tạm ứng, gửi Kế toán duyệt và trả tiền.
//
// Bài cố ý KHÔNG nêu con số trần một khoản (màn hiện số đó, lấy từ Kế toán, đổi
// được) và KHÔNG kể Kế toán duyệt ra sao — đó là việc của phòng khác.

import type { GuideBook } from "../types";

const EVERYONE = [
  "reception",
  "sale",
  "photo",
  "makeup",
  "assistant",
  "retouch",
  "cskh",
  "marketing",
  "accounting",
  "qc",
  "lead",
  "exec",
] as const;

export const EXPENSE_CLAIMS_GUIDES: GuideBook = {
  my_expense_claims: {
    purpose:
      "Khai những khoản bạn tự bỏ tiền (hoặc trừ vào tạm ứng) cho việc của công ty để Kế toán duyệt và trả lại.",
    where: [
      'Đầu trang: tên trang "Khoản chi của tôi" và nút "＋ Khai khoản chi".',
      'Ngay dưới đầu trang: khối "Tổng theo tháng (theo ngày mua)" — mỗi tháng một thẻ ghi Đã khai, Đã duyệt, Đã trả và số khoản chưa có hoá đơn; nút "Xem các tháng trước ▾" mở các tháng cũ hơn.',
      "Thân trang: các khoản bạn đã khai, mới nhất ở trên. Điện thoại hiện từng thẻ; máy tính hiện bảng, bấm một dòng để mở.",
      'Mỗi khoản có các dòng riêng: "Duyệt" (Kế toán xử lý tới đâu), "Tiền" (đã trả chưa, trả bao nhiêu) và "Hoá đơn" (hoá đơn của khoản đã về hộp thư công ty chưa — trên máy tính là cột "Hoá đơn").',
      'Trong hộp khai: ô "Loại chi", "Mô tả", "Số tiền", "Ngày phát sinh", "Cơ sở", "Thương hiệu", khối "Tiền ở đâu ra", khối "Hoá đơn (không bắt buộc)" với dòng dặn hộp thư và hai ô "Tên hoặc mã số thuế bên bán", "Ký hiệu + số hoá đơn (nếu đã có)", khối "Giấy tờ" với nút "📷 Thêm ảnh / PDF" và "🧾 Thêm HĐĐT (.xml)", nút "Gửi Kế toán" ở đáy.',
      'Mở một khoản: mã khoản, hai dòng trạng thái, "Giấy tờ đã đính" (bấm để xem lại), nút "Rút khoản này" và — khi khoản bị trả lại, từ chối, hết hạn hoặc đã rút — nút "Khai lại từ khoản này".',
    ],
    howItWorks: [
      "Trang chỉ có khoản của chính bạn; không ai khác mở được khoản của bạn ở đây.",
      'Bấm "Gửi Kế toán" là khoản được lưu ngay; nếu đường truyền chậm, khoản hiện "Đang chờ gửi sang Kế toán" và tự gửi tiếp — không cần bấm lại.',
      "Màn chặn ngay khi số tiền vượt trần một khoản (số trần ghi ở đầu hộp khai), khi ngày phát sinh sau hôm nay, hoặc khi chưa có ảnh hay PDF nào.",
      "Tệp XML hoá đơn điện tử đi kèm được nhưng không thay được ảnh — hoá đơn phải nhìn thấy được bằng mắt.",
      "Loại chi do Kế toán phát; danh mục đổi thì hộp khai tự đổi theo.",
      "Rút được khi Kế toán chưa nhận hồ sơ. Khi đã nhận, nút rút khoá lại và ghi lý do.",
      '"Khai lại từ khoản này" mở hộp khai mới đã điền sẵn chữ và giấy tờ của khoản cũ, với mã khoản mới; khoản cũ giữ nguyên.',
      "Trạng thái và tiền tự cập nhật khi Kế toán xử lý; bạn không phải báo ai.",
      "Mọi khoản đều cần hoá đơn. Lúc mua, dặn bên bán xuất hoá đơn về đúng hộp thư ghi trong hộp khai; Kế toán đọc hộp thư, tự tìm hoá đơn khớp với khoản của bạn rồi xác nhận — dòng Hoá đơn tự đổi.",
      "Hai ô bên bán và số hoá đơn không bắt buộc: bên bán chưa xuất thì để trống. Có số hoá đơn thì Kế toán khớp nhanh hơn.",
      "Qua hết ngày hôm sau ngày mua mà hoá đơn chưa về, dòng Hoá đơn chuyển đỏ và đếm số ngày quá hạn — lúc đó nhờ bên bán xuất lại về hộp thư công ty. Đã được trả tiền rồi vẫn cần hoá đơn.",
      "Tổng theo tháng tính theo ngày mua, không tính khoản đã rút.",
    ],
    statuses: [
      {
        label: "Đang chờ gửi sang Kế toán",
        meaning: "Khoản đã lưu, đang chờ gửi đi. Không phải bấm lại.",
      },
      {
        label: "Đã gửi, Kế toán chờ đủ giấy tờ",
        meaning: "Kế toán đã nhận khoản, đang chờ giấy tờ. Lúc này còn rút được.",
      },
      { label: "Kế toán đang xử lý", meaning: "Kế toán đã nhận hồ sơ; không rút được nữa." },
      { label: "Đã duyệt", meaning: "Khoản được chấp nhận; xem dòng Tiền để biết đã trả chưa." },
      {
        label: "Bị trả lại",
        meaning: "Cần bổ sung hoặc sửa — đọc lời Kế toán ngay dưới dòng Duyệt, rồi khai lại.",
      },
      { label: "Bị từ chối", meaning: "Khoản không được chấp nhận; lý do ghi dưới dòng Duyệt." },
      {
        label: "Hết hạn — thiếu giấy quá 7 ngày, khai lại",
        meaning: "Giấy tờ không đủ trong hạn nên khoản đóng; khai lại nếu còn cần.",
      },
      { label: "Đã rút", meaning: "Bạn đã rút khoản; không còn dòng Tiền." },
      {
        label: "Gửi không được — đã báo người vận hành",
        meaning:
          "Khoản đã lưu nhưng gửi đi bị kẹt; người vận hành đang xử lý, bạn không cần khai lại.",
      },
      {
        label: "Chưa trả / Đã trả … / Đã trả đủ …",
        meaning: "Dòng Tiền: Kế toán đã trả cho khoản này bao nhiêu và ngày nào.",
      },
      {
        label: "Đã có hoá đơn",
        meaning: "Kế toán đã xác nhận hoá đơn của khoản; ký hiệu, số và bên bán ghi ngay dưới.",
      },
      {
        label: "Có thể đã về — Kế toán đang soát",
        meaning:
          "Có hoá đơn trong hộp thư giống khoản này, Kế toán đang kiểm. Chưa cần đòi bên bán.",
      },
      {
        label: "Đang chờ bên bán xuất",
        meaning: "Chưa thấy hoá đơn nhưng còn trong hạn (tới hết ngày hôm sau ngày mua).",
      },
      {
        label: "Chưa có hoá đơn — quá hạn N ngày",
        meaning:
          "Màu đỏ: quá hạn mà hoá đơn chưa về. Nhờ bên bán xuất về hộp thư công ty ghi ngay dưới.",
      },
      {
        label: "Bên bán không xuất được hoá đơn",
        meaning: "Kế toán đã ghi nhận bên bán không xuất được (lý do ghi dưới); không cần đòi nữa.",
      },
      {
        label: "Khoản trước ngày theo dõi hoá đơn",
        meaning:
          "Thẻ tháng của các khoản khai trước khi Kế toán theo dõi hoá đơn; các khoản đó không có dòng Hoá đơn.",
      },
    ],
    blocks: [
      {
        for: [...EVERYONE],
        title: "Khai một khoản chi",
        steps: [
          'Bấm "＋ Khai khoản chi".',
          "Chọn loại chi, ghi mô tả ngắn: chi cho việc gì, ở đâu.",
          "Gõ số tiền đúng như trên hoá đơn và chọn ngày phát sinh.",
          "Kiểm cơ sở và thương hiệu — hai ô này điền sẵn theo hồ sơ của bạn, đổi nếu khoản chi cho nơi khác.",
          'Chọn "Tôi tự bỏ tiền", hoặc "Trừ vào tạm ứng" rồi chọn khoản ứng.',
          'Ở khối "Hoá đơn": gõ tên hoặc mã số thuế bên bán; bên bán đã xuất hoá đơn thì gõ thêm ký hiệu và số (ví dụ 1C26TGT 166). Chưa có thì để trống.',
          'Bấm "📷 Thêm ảnh / PDF" chụp hoá đơn, chọn loại giấy tờ cho từng tệp; có hoá đơn điện tử thì bấm "🧾 Thêm HĐĐT (.xml)".',
          'Bấm "Gửi Kế toán".',
        ],
        troubles: [
          "Nút Gửi Kế toán mờ: đọc dòng chữ đỏ ngay dưới nút hoặc dưới ô bị sai, sửa rồi bấm lại.",
          "Báo tệp nặng quá: chụp lại ảnh nhẹ hơn hoặc gửi bản PDF.",
          "Hộp khai báo chưa nhận được danh mục loại chi: chưa khai được lúc này, báo bộ phận kỹ thuật.",
          "Tải tệp giữa chừng bị lỗi: bấm Gửi Kế toán lần nữa — tệp đã lên không tải lại.",
          "Báo không đọc được số hoá đơn: gõ ký hiệu rồi số, cách nhau một dấu cách — hoặc xoá ô đó đi nếu chưa có hoá đơn.",
        ],
      },
      {
        for: [...EVERYONE],
        title: "Theo dõi, rút và khai lại",
        steps: [
          "Mở trang, nhìn hai dòng Duyệt và Tiền của từng khoản.",
          'Bị trả lại: mở khoản, đọc lời Kế toán, bấm "Khai lại từ khoản này", sửa chỗ cần sửa rồi gửi.',
          'Khai nhầm mà Kế toán chưa nhận hồ sơ: mở khoản, bấm "Rút khoản này", ghi lý do, bấm "Rút khoản".',
          "Bấm một giấy tờ trong khoản để xem lại; tệp XML tải về máy.",
          "Thấy dòng Hoá đơn đỏ: nhắn bên bán xuất hoá đơn về hộp thư công ty; hoá đơn về thì dòng tự đổi.",
          "Đầu tháng xem thẻ tháng trước: còn khoản chưa có hoá đơn thì đi đòi cho đủ.",
        ],
        troubles: [
          "Nút Rút khoản này mờ: Kế toán đã nhận hồ sơ — cần sửa thì nhắn kế toán.",
          "Đã duyệt mà dòng Tiền vẫn Chưa trả: bình thường, duyệt và trả là hai bước riêng.",
          "Đứng mãi ở Đang chờ gửi sang Kế toán: báo bộ phận kỹ thuật, không khai trùng khoản mới.",
          "Bên bán nói đã xuất mà dòng vẫn đỏ: nhờ bên bán gửi lại đúng hộp thư, hoặc nhắn kế toán kèm số hoá đơn.",
        ],
      },
    ],
    askWho: "Hỏi về khoản đã gửi thì nhắn kế toán; màn báo lỗi lạ thì báo bộ phận kỹ thuật.",
  },
};
